import type { IpnsString } from '@wovin/core/applog'
import type { CoPubKeyRecord, IShare, ISubscription } from '@wovin/core/pubsub'
import { dateNowIso } from '@wovin/core/applog'
import { arrayBufferToBase64 } from '@wovin/utils'
import { Logger } from 'besonders-logger'
import { generateKeyPairFromSeed, privateKeyToProtobuf } from '@libp2p/crypto/keys'
import * as W3Name from 'w3name'
import { aesUtils, derivePublicationKeySeed, deriveSharedEncryptionKey } from '@wovin/crypto'
import { createAESkeyForShare } from '../data/agent/AgentCrypto'
import { useAgent } from '../data/agent/AgentState'
import { getSubOrShare } from '../data/agent/utils-agent'
import { insertApplogsInAppDB } from '../data/ApplogDB'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.DEBUG, { prefix: '[sub-pub]' })

export async function upsertSubscription(
	ipns: IpnsString,
	subFields:
		& Partial<Pick<ISubscription, 'name' | 'lastPull' | 'encryptedFor' | 'lastCID' | 'autopull' | 'lastPullAttempt' | 'lastApplogCID'>>
		& { encryptedBy?: string; derivationJWKstring?: string } = {},
) {
	LOG('Upserting subscription:', { ipns, subFields })
	const agent = useAgent()

	const { ag } = agent

	const subscriptionObj: ISubscription = {
		id: ipns,
		createdAt: dateNowIso(),
		name: ipns.slice(-7),
		isDeleted: false,
		lastPull: null,
		autopull: false,
		...subFields,
	}
	if (subFields.encryptedBy && subFields.derivationJWKstring) {
		let privateKey: CryptoKey, remotePubDerivationKey: CryptoKey
		try {
			privateKey = (await agent.getOrSetDerivationKeypair()).privateKey
		} catch (error) {
			throw new Error('failed to get private key')
		}
		try {
			remotePubDerivationKey = await importPublicDerivationKey(subFields.derivationJWKstring)
			subscriptionObj.encryptedWith = await deriveSharedEncryptionKey(privateKey, remotePubDerivationKey, true)

			// doTestEncryptDecrypt(subscriptionObj) // ! This derived key works in the test... but not with the payloads from ipfs
		} catch (error) {
			throw new Error('failed to importPublicDerivationKey')
		}
		subscriptionObj.publishedBy = subFields.encryptedBy
		subscriptionObj.encryptedFor = ag
		subscriptionObj.name = `${subFields.encryptedBy} => ${ag}`
	}
	if (agent.subscriptions.find(s => s.id === subscriptionObj.id)) {
		await agent.updateSub(subscriptionObj.id, subscriptionObj as ISubscription) // update the subscription
	} else {
		await agent.addSub(subscriptionObj as ISubscription) // insert the subscription
	}
	DEBUG('[upsertSubscription]', subscriptionObj)

	return subscriptionObj
}

export async function addShare(
	pubFields: Partial<Pick<IShare, 'name' | 'encryptedFor' | 'selectors'>> & { derivationJWKstring?: string } = {},
) {
	const agent = useAgent()
	const { ag: publishedBy, getOrSetDerivationKeypair: getDerivationKeypair } = agent
	const { privateKey, publicKey } = (await getDerivationKeypair()) as CryptoKeyPair

	const counterFromLocalStorage = localStorage.getItem('agent.pubCounter')
	const pubCounter = counterFromLocalStorage ? +counterFromLocalStorage + 1 : 1
	localStorage.setItem('agent.pubCounter', pubCounter.toFixed(0))
	LOG('Adding share:', { publishedBy, pubFields })
	DEBUG('creating new pubkey derivation via mnemkey')
	const newPKseed: Uint8Array = await derivePublicationKeySeed({ privateKey, publicKey }, `${publishedBy}+${pubCounter}`)
	VERBOSE({ newPKseed })
	const newPriEd25519 = await generateKeyPairFromSeed('Ed25519', newPKseed)
	VERBOSE({ newPriEd25519 })

	// DEBUG('creating new WritableName')
	// Use W3Name.from() to properly convert from old libp2p-crypto format to new @libp2p/crypto format
	const newName: W3Name.WritableName = await W3Name.from(privateKeyToProtobuf(newPriEd25519))
	DEBUG('new pubkey:', { newPKseed, newPriEd25519, newName })

	const { name = `pub ${newName.toString().slice(-7)}`, encryptedFor, derivationJWKstring } = pubFields
	// Store protobuf-encoded key bytes so W3Name.from() can reconstruct it later
	const pk = privateKeyToProtobuf(newPriEd25519)
	const newShare: IShare = {
		id: newName.toString(),
		name,
		pk,
		publishedBy,
		pubCounter,
		createdAt: dateNowIso(),
		lastPush: null,
		autopush: false,
	}
	if (encryptedFor && derivationJWKstring) {
		newShare.name = `${newShare.name}=>${encryptedFor}`
		newShare.encryptedFor = encryptedFor
		const remotePubDerivationKey = await importPublicDerivationKey(derivationJWKstring)

		newShare.encryptedWith = await deriveSharedEncryptionKey(privateKey, remotePubDerivationKey, true)
	}

	DEBUG('[addShare]', { newShare, newName })
	await agent.addShare(newShare)
	return newShare
}

export async function updateShareEncryption(
	pubFields: Partial<Pick<IShare, 'id' | 'encryptedFor' | 'sharedAgents' | 'selectors'>> & { derivationJWKstring?: string } = {},
) {
	LOG('Updating share encryption:', pubFields)
	const newName: W3Name.WritableName = await W3Name.create() // ? new ipns name when updating encryption? // TODO deterministic
	const agent = useAgent()
	const { ag: publishedBy, getKnownAgents, crypto } = agent
	const { privateKey } = await agent.getOrSetDerivationKeypair()
	const { id, encryptedFor, sharedAgents } = pubFields
	const pk = newName.key.raw
	const existingShare = getSubOrShare(pubFields.id) as IShare
	const shareUpdate: Partial<IShare> = {}
	const knownAgentsMap = getKnownAgents().get()
	if (sharedAgents) { // multiple agents
		shareUpdate.encryptedFor = null
		shareUpdate.encryptedWith = null
		shareUpdate.sharedAgents = sharedAgents
		// const unifiedSharedAESkey = yield createMultiAgentEncryptionKey() // non deterministic
		const unifiedSharedAESkey = await createAESkeyForShare(existingShare)
		DEBUG({ unifiedSharedAESkey })
		const exportedKey = await globalThis.crypto.subtle.exportKey('raw', unifiedSharedAESkey)
		shareUpdate.sharedKey = unifiedSharedAESkey // ? here we could reimport the shared key as not extractable
		// then WebCrypto would hide it (but its futile for reasons:)
		// 1. if its not extractable then we can't export it to reincrypt it for others
		// 2. if thats the case, we might as well not save it in IDB (but only encrypted for ourselves in the applog)
		// TODO solid strategy for key rotation, saving, sharing, managing
		const keyBase64 = arrayBufferToBase64(exportedKey)
		DEBUG('sharedKey', { exportedKey, keyBase64 })
		shareUpdate.sharedKeyMap = new Map()
		for (const eachAgent of sharedAgents) {
			const eachJWK = knownAgentsMap.get(eachAgent).jwkd as string
			if (eachJWK) {
				const eachPubKey = await importPublicDerivationKey(eachJWK)
				const derivedKeyToEncryptSharedKeyWith = await deriveSharedEncryptionKey(privateKey, eachPubKey, true)
				const encryptedSharedKey = await (crypto as any).aes.encrypt(exportedKey, derivedKeyToEncryptSharedKeyWith, 'AES-GCM')
				shareUpdate.sharedKeyMap.set(eachAgent, arrayBufferToBase64(encryptedSharedKey))
				DEBUG('midloop', { shareUpdate })
			} else {
				ERROR('unknown JWK - weirdness', { knownAgentsMap, eachAgent })
			}
		}
	} else if (encryptedFor) { // single agent
		WARN('deprecated')
		// pubUpdate.encryptedFor = encryptedFor
		// const derivationJWKstring = knownAgentsMap.get(encryptedFor).jwkd as string
		// if (derivationJWKstring) {
		// 	const remotePubDerivationKey = yield importPublicDerivationKey(derivationJWKstring)

		// 	pubUpdate.encryptedWith = yield deriveSharedEncryptionKey(privateKey, remotePubDerivationKey, true)
		// 	if (pubUpdate.encryptedWith) {}
		// } else {
		// }
	} else {
		ERROR('encryptedFor unknown', { encryptedFor, knownAgentsMap })
	}

	await agent.updateShare(id, shareUpdate)
	const traceObj = encryptedFor ? { encryptedFor } : { sharedAgents }
	LOG('[updateShare]', { shareUpdate }, traceObj)

	return shareUpdate
}

/**
 * Enable co-publishing for a share: encrypts `share.pk` per-co-publisher via ECDH so every
 * co-publisher can reconstruct the IPNS private key, writes a `share/copubkey` encrypted applog
 * into the local applog DB (so it ships in the next snapshot push), and marks the share as a
 * co-publish share with the originator recorded.
 *
 * The plaintext `share.pk` is never written in the clear — only the AES-GCM-encrypted `encPk` plus
 * a per-co-publisher ECDH-encrypted copy of the one-shot AES key are persisted (see
 * `CoPubKeyRecord`). A security assertion guards against accidentally serializing raw `pk` bytes.
 */
export async function enableCoPublish(shareId: string, coPublisherAgs: string[]) {
	LOG('Enabling co-publish:', { shareId, coPublisherAgs })
	const agent = useAgent()
	const { ag, getKnownAgents, crypto } = agent
	const { privateKey } = await agent.getOrSetDerivationKeypair()

	const existingShare = getSubOrShare(shareId) as IShare
	if (!existingShare?.pk) throw ERROR('[enableCoPublish] share not found or missing pk', { shareId })
	if (!coPublisherAgs.length) throw ERROR('[enableCoPublish] no co-publishers provided', { shareId })

	const knownAgentsMap = getKnownAgents().get()

	// One-shot AES-GCM key used only to encrypt share.pk for this co-publish setup.
	// Extractable so we can export the raw bytes and re-encrypt them per co-publisher via ECDH.
	const oneShotAesKey = await createMultiAgentEncryptionKey()
	const exportedAesKey = await globalThis.crypto.subtle.exportKey('raw', oneShotAesKey)
	DEBUG('encrypting', { oneShotAesKey })

	// aesEncrypt returns IV(16) || ciphertext; split so the record carries the iv explicitly.
	const encPkFull = new Uint8Array(await aesUtils.encrypt(existingShare.pk, oneShotAesKey, 'AES-GCM'))
	const ivBytes = encPkFull.slice(0, 16)
	const cipherBytes = encPkFull.slice(16)

	const encKeyMap: Record<string, string> = {}
	for (const eachAgent of coPublisherAgs) {
		const eachJWK = knownAgentsMap.get(eachAgent)?.jwkd as string | undefined
		if (!eachJWK) {
			throw ERROR(
				`[enableCoPublish] co-publisher ${eachAgent} has no jwkd — add agent to roster / known agents first`,
				{ coPublisherAgs, knownAgentsMap },
			)
		}
		const eachPubKey = await importPublicDerivationKey(eachJWK)
		const derivedKeyToEncryptAesKeyWith = await deriveSharedEncryptionKey(privateKey, eachPubKey, true)
		DEBUG('encrypting', { eachPubKey })
		const encryptedAesKey = await aesUtils.encrypt(exportedAesKey, derivedKeyToEncryptAesKeyWith, 'AES-GCM')
		encKeyMap[eachAgent] = arrayBufferToBase64(encryptedAesKey)
		DEBUG('[enableCoPublish] encrypted AES key for co-publisher', { eachAgent })
	}

	const coPubKeyRecord: CoPubKeyRecord = {
		encKeyMap,
		iv: arrayBufferToBase64(ivBytes),
		encPk: arrayBufferToBase64(cipherBytes),
		version: 1,
	}

	// Security invariant: share.pk must never be serialized in the clear. Assert the serialized
	// `vl` of the share/copubkey log contains the encrypted record, not the raw pk bytes.
	const vlString = JSON.stringify(coPubKeyRecord)
	const rawPkBase64 = arrayBufferToBase64(existingShare.pk)
	if (vlString.includes(rawPkBase64)) {
		throw ERROR('[enableCoPublish] SECURITY: raw share.pk leaked into copubkey record vl', { shareId })
	}

	// Write the `share/copubkey` applog into the local DB so it's included in the next push.
	// `en` is the reserved `share:<id>` entity; `ag` is the originator (tracked as required).
	insertApplogsInAppDB({
		en: `share:${existingShare.id}`,
		at: 'share/copubkey',
		ag,
		vl: vlString,
	})

	// Mark the share as a co-publish share and record the originator + co-publishers.
	await agent.updateShare(shareId, {
		coPublish: true,
		coPublishers: coPublisherAgs,
		originatorAg: ag,
	})

	LOG('[enableCoPublish] done', { shareId, coPublisherAgs, originatorAg: ag })
	return coPubKeyRecord
}
export async function importPublicDerivationKey(derivationJWKstring: string) {
	const parsedPublicJWK = JSON.parse(derivationJWKstring)
	DEBUG({ derivationJWKstring, parsedPublicJWK })

	return await globalThis.crypto.subtle.importKey(
		'jwk',
		parsedPublicJWK,
		{ name: 'ECDH', namedCurve: 'P-256' },
		true,
		[],
	)
}

export async function createMultiAgentEncryptionKey() {
	const newKey = await globalThis.crypto.subtle.generateKey(
		{
			name: 'AES-GCM',
			length: 256,
		},
		true,
		['encrypt', 'decrypt'],
	)
	DEBUG('created new key', { newKey })
	return newKey
}
